import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { swarmApi } from '../utils/api'
import { useSwarmStore } from '../stores/swarm'
import toast from 'react-hot-toast'
import type { CreateSwarmRequest } from '../types'

export function useSwarms(params?: { status?: string; page?: number }) {
  const { setSwarms, setLoading, setError } = useSwarmStore()

  return useQuery({
    queryKey: ['swarms', params],
    queryFn: async () => {
      setLoading(true)
      try {
        const response = await swarmApi.getAll(params)
        setSwarms(response.data.swarms)
        return response.data
      } catch (error) {
        setError('Failed to load swarms')
        throw error
      } finally {
        setLoading(false)
      }
    },
  })
}

export function useSwarm(id: string) {
  const { setSelectedSwarm } = useSwarmStore()

  return useQuery({
    queryKey: ['swarm', id],
    queryFn: async () => {
      const response = await swarmApi.getById(id)
      setSelectedSwarm(response.data)
      return response.data
    },
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll more frequently while swarm is active
      const data = query?.state?.data as any
      if (data?.status && ['planning', 'executing', 'synthesizing'].includes(data.status)) {
        return 2000 // 2 seconds
      }
      return false // Don't poll
    },
  })
}

export function useCreateSwarm() {
  const queryClient = useQueryClient()
  const { addSwarm } = useSwarmStore()

  return useMutation({
    mutationFn: async (data: CreateSwarmRequest) => {
      const response = await swarmApi.create(data)
      return response.data
    },
    onSuccess: (data) => {
      addSwarm(data.swarm)
      queryClient.invalidateQueries({ queryKey: ['swarms'] })
      toast.success('Swarm created successfully!')
      return data
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create swarm')
    },
  })
}

export function useDeleteSwarm() {
  const queryClient = useQueryClient()
  const { removeSwarm } = useSwarmStore()

  return useMutation({
    mutationFn: async (id: string) => {
      await swarmApi.delete(id)
      return id
    },
    onSuccess: (id) => {
      removeSwarm(id)
      queryClient.invalidateQueries({ queryKey: ['swarms'] })
      toast.success('Swarm deleted')
    },
    onError: () => {
      toast.error('Failed to delete swarm')
    },
  })
}

export function useSwarmAgents(swarmId: string) {
  return useQuery({
    queryKey: ['swarm-agents', swarmId],
    queryFn: async () => {
      const response = await swarmApi.getAgents(swarmId)
      return response.data
    },
    enabled: !!swarmId,
  })
}
